import { CurrencyPipe, IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { inject } from '@angular/core';

import { MarketDisplayFallbackService, SportsMarketGroupingFallbackService } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { LAZY_STYLES_CONFIG } from '@frontend/sports/common/core/feature/configs';
import { SCROLL_ADAPTER_CONFIG, ScrollAdapterConfig } from '@frontend/sports/common/core/feature/scroll-adapter';
import { CURRENT_THEME_TOKEN, IS_ROBOT_TOKEN, SportsTheme } from '@frontend/sports/common/ui/shared';
import { provideDataRefresh } from '@frontend/sports/data-refresh/feature';
import { PatchedMediaObserver, provideLayout } from '@frontend/sports/layout/feature';
import { CustomLoadingIndicatorExtendedService, CustomLoadingIndicatorService, LOADING_INDICATOR } from '@frontend/sports/loading-indicator-feature';
import { MODAL_WINDOW_STACK_TOKEN } from '@frontend/sports/modal/feature';
import { MODAL_WINDOW_CONFIG } from '@frontend/sports/modal/feature/provider';
import { FallbackImage, PARTICIPANT_IMAGE_CONFIG } from '@frontend/sports/participant-image/feature/provider';
import {
    PrettyUrlLanguageSwitcherProvider,
    PrettyUrlPageViewDataProvider,
    PrettyUrlRouteProcessor,
} from '@frontend/sports/routing/pretty-urls/feature';
import { provideTracking } from '@frontend/sports/tracking/feature/network-profiler';
import { provideUser } from '@frontend/sports/user/feature';
import { WIDGET_EXPERIMENTS } from '@frontend/sports/widget/core/feature';
import { DeviceService, MediaQueryService, PAGE_VIEW_DATA_PROVIDER, Page, ROUTE_PROCESSOR, STORE_PREFIX } from '@frontend/vanilla/core';
import { LANGUAGE_SWITCHER_URLS_PROVIDER } from '@frontend/vanilla/features/language-switcher';
import { RUN_AFTER_LCP_TOKEN, RunAfterLcpType } from '@frontend/vanilla/features/tracking';
import { provideNgxPopperjsOptions } from 'ngx-popperjs';

import { provideDigitalBetslipIntegration } from '../../betslip/integration/betslip-digital-integration.provider';
import { provideWebBridge } from '../../betslip/integration/web-bridge/web-bridge.provider';
import { AccaBoostRefreshTriggerService } from '../acca-boost-base/acca-boost-refresh-trigger.service';
import { ACCA_BOOST_REFRESH_TRIGGER } from '../acca-boost-base/acca-boost-token-provider.service';
import { provideBestOdds } from '../best-odds-guarantee/best-odds.provider';
import { provideSportsWebBootstrap } from '../bootstrap/sports-web-bootstrap.provider';
import { provideCompetitionWidget } from '../competition-widget/competition-widget-provider';
import { provideEsports } from '../esports/esports.provider';
import { FavouritesGridWidgetExperimentService } from '../favourites-grid-widget/favourites-grid-widget-experiment.service';
import {
    provideFavouritesWidgetSkeleton,
    provideFavouritesWidgetSkeletonCustomLoad,
} from '../favourites-grid-widget/favourites-widget-skeleton.provider';
import { provideGrid } from '../grid/grid.provider';
import { provideMarqueeWidgetSkeleton } from '../highlights-marquee/marquee-widget-skeleton.provider';
import { provideHome } from '../home/home.provider';
import { provideSportsHttpInterceptors } from '../http-interceptors';
import { provideMarketSubTypeLogoHideStrategy } from '../marketsubtype-logo/marketsubtype-logo-hide.provider';
import { provideMedia } from '../media/media.provider';
import { ModalStackService } from '../modal/modal-stack.service';
import { provideNativeApi } from '../native-api/native-api.provider';
import { provideNgrxReports } from '../ngrx-reports/ngrx-reports.provider';
import { provideNumpad } from '../numpad/numpad.provider';
import { providePlayerStatistics } from '../option-group/player-statistics/player-statistics.provider';
import { provideOptionSheetView } from '../option-sheet-view/option-sheet-view.provider';
import { provideRunAfterLcp } from '../perf-cdk/run-after-lcp.provider';
import { RunAfterLcpService } from '../perf-cdk/run-after-lcp.service';
import { RootInjector } from '../router/root-injector.service';
import { provideSheetViewCore } from '../sheet-view-core/sheet-view.provider';
import { provideRootStateStorage } from '../store-persist/state-storage-root.provider';
import { provideUiManager } from '../ui-manager/ui-manager.provider';

export const SCROLL_ADAPTER_CONFIG_OVERRIDE: ScrollAdapterConfig = {
    disableHorizontalScrollOnDesktop: true,
};

/**
 * Provides services needed for running the Sports-Web application as standalone or in host-app integration.
 * This should include all regular services used by the application.
 * These services will be provided as root in standalone mode and non-root in host-app integration.
 * @returns Providers needed for running Sports-Web.
 */
export function provideSportsWebCore() {
    return [
        provideDigitalBetslipIntegration(),
        provideNgxPopperjsOptions(),
        provideEsports(),
        provideUser(),
        provideGrid(),
        provideSheetViewCore(),
        provideOptionSheetView(),
        provideTracking(),
        provideUiManager(),
        provideMedia(),
        provideHttpClient(withInterceptorsFromDi(), withInterceptors(provideSportsHttpInterceptors()), withFetch()),
        provideCompetitionWidget(),
        provideNgrxReports(),
        provideLayout(),
        provideNumpad(),
        provideBestOdds(),
        provideWebBridge(),
        provideDataRefresh(),
        provideNativeApi(),
        provideSportsWebBootstrap(),
        provideHome(),
        provideRootStateStorage(),
        provideMarketSubTypeLogoHideStrategy(),
        { provide: STORE_PREFIX, useValue: 'ms2.' },
        CurrencyPipe,
        RootInjector,
        provideFavouritesWidgetSkeleton(),
        provideMarqueeWidgetSkeleton(),
        providePlayerStatistics(),
        provideFavouritesWidgetSkeletonCustomLoad(),
        { provide: WIDGET_EXPERIMENTS, useClass: FavouritesGridWidgetExperimentService, multi: true },
        { provide: MarketDisplayFallbackService, useClass: SportsMarketGroupingFallbackService },
        { provide: MediaQueryService, useClass: PatchedMediaObserver },
        {
            provide: CustomLoadingIndicatorService,
            useExisting: CustomLoadingIndicatorExtendedService,
        },
        {
            provide: LANGUAGE_SWITCHER_URLS_PROVIDER,
            useClass: PrettyUrlLanguageSwitcherProvider,
        },
        { provide: LOADING_INDICATOR, useValue: '' },
        {
            provide: PAGE_VIEW_DATA_PROVIDER,
            useExisting: PrettyUrlPageViewDataProvider,
            multi: true,
        },
        {
            provide: ROUTE_PROCESSOR,
            useClass: PrettyUrlRouteProcessor,
            multi: true,
        },
        {
            provide: SCROLL_ADAPTER_CONFIG,
            useValue: SCROLL_ADAPTER_CONFIG_OVERRIDE,
        },
        {
            provide: ACCA_BOOST_REFRESH_TRIGGER,
            useClass: AccaBoostRefreshTriggerService,
        },
        {
            provide: MODAL_WINDOW_STACK_TOKEN,
            useClass: ModalStackService,
        },
        {
            provide: IMAGE_LOADER,
            useValue: (config: ImageLoaderConfig) => {
                let result = config.src;
                if (result.indexOf('?') < 0) {
                    result += '?';
                } else {
                    result += '&';
                }

                if (!config.src.endsWith('.svg') && config.src.indexOf('scmedia') >= 0 && config.width) {
                    result += `w=${config.width}&`;
                }

                if (result.endsWith('?') || result.endsWith('&')) {
                    result = result.slice(0, result.length - 1);
                }

                return result;
            },
        },
        provideRunAfterLcp(),
        {
            provide: RUN_AFTER_LCP_TOKEN,
            useFactory: () => {
                const runAfterLcpService = inject(RunAfterLcpService);
                return {
                    runAfterLcp(fn: () => void) {
                        runAfterLcpService.runAfterLcp(fn);
                    },
                } satisfies RunAfterLcpType;
            },
        },
        { provide: CURRENT_THEME_TOKEN, useFactory: (page: Page) => page.theme as SportsTheme, deps: [Page] },
        { provide: IS_ROBOT_TOKEN, useFactory: (device: DeviceService) => device.isRobot, deps: [DeviceService] },
        {
            provide: MODAL_WINDOW_CONFIG,
            deps: [EpcotConfig],
            useFactory: (epcot: EpcotConfig) => {
                return {
                    animatePopupDismiss: epcot.isEnabled,
                    showCloseIcon: !epcot.isEnabled,
                };
            },
        },
        {
            provide: LAZY_STYLES_CONFIG,
            deps: [EpcotConfig],
            useFactory: (epcot: EpcotConfig) => {
                return {
                    isEpcotEnabled: epcot.isEnabled,
                };
            },
        },
        {
            provide: PARTICIPANT_IMAGE_CONFIG,
            deps: [EpcotConfig],
            useFactory: (epcot: EpcotConfig) => {
                return {
                    fallbackImage: epcot.isEnabled ? FallbackImage.EpcotDefault : FallbackImage.Default,
                };
            },
        },
    ];
}
